import { LinkOutlined } from '@ant-design/icons';
import * as AllIcons from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link, useModel, Navigate, getIntl } from '@umijs/max';
import React from 'react';
import { tracker, reporter } from './utils/tracker';
import { initPvTracker } from '@ai-saas/tracker';
// import type { TrackEvent } from '@ai-saas/tracker';
import { heartbeat } from '@/services/ant-design-pro/tracker';
import { getClickEventByPath, initRouteMaps, getMenuPathByRoute } from '@/utils/route-tracker';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import RoutePermissionGuard from '@/components/RoutePermissionGuard';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import { initMenuLocales, cleanLegacyMenuLocaleCache } from '@/utils/i18n-utils';
import { cleanupRedundantColumnsStateKeys } from '@/utils/comm';
import { App, Button } from 'antd';
import {
  setMessageInstance,
  setNotificationInstance,
  setModalInstance,
  message,
} from '@/utils/message-manager';
import { currentUser } from '@/services/ant-design-pro/api';
import { migrateOldCache, initAppCache } from '@/utils/app-cache';
import { isStandaloneApp } from '@/utils/env';
import { getAppGradient, getAppInitial } from '@/utils/appAvatar';
import { AliveScope } from 'react-activation';
import MultiTab from '@/components/MultiTab';
import KeepAliveWrapper from '@/components/KeepAlive';
import ThemeProvider from '@/components/ThemeProvider';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

let trackerInitialized = false;

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  menuOpenKeys?: string[];
}> {
  cleanupRedundantColumnsStateKeys();

  // 初始化 tracker（只执行一次）
  if (!trackerInitialized) {
    // tracker.setReporter((events: TrackEvent[]) => track(events));
    reporter.setHeartbeatRequest(heartbeat);
    initPvTracker(tracker);
    reporter.schedule(tracker);
    trackerInitialized = true;
  }

  const redirectToLogin = (errMsg?: string) => {
    if (errMsg) message.error(errMsg);
    localStorage.removeItem('user_info');
    localStorage.removeItem('token');
    reporter.stopHeartbeat();
    history.push(loginPath);
  };

  const fetchUserInfo = async () => {
    try {
      const res = await currentUser({ skipErrorHandler: true });
      if (res.code !== '200') {
        redirectToLogin(res.msg);
        return undefined;
      }
      if (!res.data) {
        redirectToLogin();
        return undefined;
      }
      const userInfoStr = localStorage.getItem('user_info');
      const merged = userInfoStr
        ? { ...(JSON.parse(userInfoStr) as API.CurrentUser), ...res.data }
        : res.data;
      localStorage.setItem('user_info', JSON.stringify(merged));
      return merged;
    } catch (error: any) {
      if (error.response?.status === 401) {
        redirectToLogin();
        return undefined;
      }
    }
    return undefined;
  };

  // 清除无 userId 前缀的旧格式菜单国际化缓存
  cleanLegacyMenuLocaleCache();

  // 有 token 即启动心跳，不依赖 fetchUserInfo（startHeartbeat 内部会先 stop，保证单实例）
  if (localStorage.getItem('token')) {
    reporter.startHeartbeat();
  }

  // 有 token 就获取用户信息，无论是否白名单页面
  if (localStorage.getItem('token')) {
    const currentUser = await fetchUserInfo();
    if (currentUser?.userId) {
      tracker.setUser(currentUser.userId);
      initAppCache(currentUser.userId);
      migrateOldCache();
      // 初始化菜单国际化（按用户隔离）
      initMenuLocales(currentUser.userId);
    } else {
      initMenuLocales();
    }
    const savedTheme = localStorage.getItem('navTheme');
    const settings = {
      ...defaultSettings,
      ...(savedTheme ? { navTheme: savedTheme } : {}),
    } as Partial<LayoutSettings>;

    return {
      fetchUserInfo,
      currentUser,
      settings,
    };
  }

  const savedTheme = localStorage.getItem('navTheme') === 'realDark'
    ? (localStorage.setItem('navTheme', 'dark'), 'dark')
    : localStorage.getItem('navTheme');
  const settings = {
    ...defaultSettings,
    ...(savedTheme ? { navTheme: savedTheme } : {}),
  } as Partial<LayoutSettings>;

  return {
    fetchUserInfo,
    settings,
  };
}

const mapMenus = (menuList: any[]) => {
  const map: Record<string, any> = {};
  const tree: any[] = [];

  const fixIconName = (icon: string) => {
    if (!icon) {
      return '';
    }
    const upperFirst = icon.charAt(0).toUpperCase() + icon.slice(1);
    return `${upperFirst}Outlined`;
  };

  menuList.forEach((item) => {
    const iconName = fixIconName(item.icon);
    const IconComponent = (AllIcons as any)[iconName];
    map[item.menuId] = {
      path: item.path,
      name: item.menuName,
      icon: IconComponent ? React.createElement(IconComponent) : item.icon,
      children: [],
    };
  });

  menuList.forEach((item) => {
    const node = map[item.menuId];
    if (item.parentId && map[item.parentId]) {
      map[item.parentId].children.push(node);
    } else {
      tree.push(node);
    }
  });

  const cleanChildren = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.children && node.children.length === 0) {
        delete node.children;
      } else if (node.children) {
        cleanChildren(node.children);
      }
    });
  };
  cleanChildren(tree);

  return tree;
};

const standaloneMenuData = [
  {
    path: '/app',
    name: 'app',
    icon: <AllIcons.AppstoreOutlined />,
    children: [],
  },
];

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const isStandalone = isStandaloneApp();
  const isLandingPage = typeof window !== 'undefined' && window.location.pathname === '/';
  const isDark = initialState?.settings?.navTheme !== 'light';

  // 同步主题到 html 标签，自定义 data-app-theme 供全局样式使用（例如滚动条）
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-app-theme', isDark ? 'dark' : 'light');
  }

  // 动态读取路由 parentKeys，确保隐藏页面时侧边栏仍高亮正确的菜单项
  const currentPathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const menuPathname = getMenuPathByRoute(currentPathname) ?? currentPathname;

  return {
    location: { pathname: menuPathname },
    menuRender: isLandingPage ? false : undefined,
    collapsedButtonRender: false,
    menuFooterRender: isStandalone
      ? false
      : (props) => {
          const isDark = initialState?.settings?.navTheme !== 'light';
          return (
            <div
              style={{
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                padding: '0 8px',
              }}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => props?.onCollapse?.(!props?.collapsed)}
                onKeyDown={(e) => e.key === 'Enter' && props?.onCollapse?.(!props?.collapsed)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '16px 12px',
                  cursor: 'pointer',
                  borderRadius: 8,
                  color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)',
                  fontSize: 14,
                  userSelect: 'none',
                }}
              >
                {props?.collapsed ? (
                  <AllIcons.MenuUnfoldOutlined style={{ fontSize: 16 }} />
                ) : (
                  <AllIcons.MenuFoldOutlined style={{ fontSize: 16 }} />
                )}
                <span
                  style={{
                    maxWidth: props?.collapsed ? 0 : 120,
                    opacity: props?.collapsed ? 0 : 1,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: 'max-width 0.2s ease, opacity 0.15s ease',
                    display: 'inline-block',
                  }}
                >
                  {getIntl().formatMessage({ id: 'component.sider.collapse', defaultMessage: '收起导航' })}
                </span>
              </div>
            </div>
          );
        },
    collapsed: isStandalone ? true : undefined,
    menuDataRender: () => {
      if (isStandalone) {
        return standaloneMenuData;
      }
      if (initialState?.currentUser?.menuList) {
        return mapMenus(initialState.currentUser.menuList);
      }
      return [];
    },
    actionsRender: () => {
      const isDark = initialState?.settings?.navTheme !== 'light';
      return [
        // <Question key="doc" />,
        <span
          key="theme-toggle"
          title={isDark ? '切换亮色主题' : '切换暗色主题'}
          style={{ cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center' }}
          onClick={() => {
            const newTheme = isDark ? 'light' : 'realDark';
            localStorage.setItem('navTheme', newTheme);
            setInitialState((prev) => ({
              ...prev,
              settings: {
                ...prev?.settings,
                navTheme: newTheme,
              },
            }));

            // 更新自定义 data-app-theme 属性，确保全局样式（如滚动条）能准确识别暗黑模式
            if (typeof document !== 'undefined') {
              document.documentElement.setAttribute(
                'data-app-theme',
                newTheme === 'light' ? 'light' : 'dark',
              );
            }
          }}
        >
          {isDark ? <AllIcons.MoonOutlined /> : <AllIcons.SunOutlined />}
        </span>,
        <SelectLang key="SelectLang" />,
      ];
    },
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      shape: 'circle' as const,
      size: 'small' as const,
      style: {
        background: getAppGradient(initialState?.currentUser?.userNickname || ''),
        color: '#fff',
        border: 'none',
      },
      children: getAppInitial(initialState?.currentUser?.userNickname || ''),
      render: (_: any, avatarChildren: React.ReactNode) => {
        if (!initialState?.currentUser) {
          return (
            <Button
              type="primary"
              size="middle"
              style={{ borderRadius: 8, fontWeight: 600 }}
              onClick={() => history.push(loginPath)}
            >
              登录
            </Button>
          );
        }
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    // waterMarkProps: {
    //   content: initialState?.currentUser?.email,
    // },
    footerRender: isLandingPage ? false : () => <Footer />,
    breadcrumbRender: false,
    // 接管菜单展开状态：防止路由切换时 ProLayout 自动折叠所有目录
    // undefined 时 ProLayout 自行管理（初始加载），有值时切换为受控模式
    menuProps: {
      openKeys: initialState?.menuOpenKeys,
      onOpenChange: (keys: string[]) => {
        setInitialState((prev) => ({ ...prev, menuOpenKeys: keys }));
      },
    },
    onPageChange: () => {
      const { location } = history;
      // 登录页停止心跳
      if (location.pathname === loginPath) {
        reporter.stopHeartbeat();
        return;
      }
      // 如果没有登录，重定向到 login
      const token = localStorage.getItem('token');
      if (!initialState?.currentUser && !token && location.pathname !== '/') {
        history.push(loginPath);
      }
      // 路由切换时，将新路由的祖先 key merge 进受控 openKeys
      // 避免跳到新模块时其父目录未展开
      if (initialState?.menuOpenKeys !== undefined) {
        const parts = location.pathname.split('/').filter(Boolean);
        const ancestors: string[] = [];
        for (let i = 1; i < parts.length; i++) {
          ancestors.push('/' + parts.slice(0, i).join('/'));
        }
        if (ancestors.length > 0) {
          setInitialState((prev) => {
            const existing = prev?.menuOpenKeys ?? [];
            const merged = [...new Set([...existing, ...ancestors])];
            if (merged.length === existing.length) return prev;
            return { ...prev, menuOpenKeys: merged };
          });
        }
      }
      // 路由菜单点击埋点
      const clickEvent = getClickEventByPath(location.pathname);
      if (clickEvent) {
        tracker.trackClick(clickEvent.clickEventKey, clickEvent.clickEventType);
      }
    },
    links: [],
    menuHeaderRender: undefined,
    headerTitleRender: (logo, title) => (
      <a style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }} href="/">
        {logo}
        <span
          style={{
            background: 'linear-gradient(135deg, #6C7AF4 0%, #a5b4fc 50%, #D5E6F7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: 0.5,
          }}
        >
          {title}
        </span>
      </a>
    ),
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      if (isLandingPage) {
        return <div className="ka-content-landing">{children}</div>;
      }
      return (
        <AliveScope>
          <MultiTab />
          <div className="ka-content">
            <KeepAliveWrapper>{children}</KeepAliveWrapper>
          </div>
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </AliveScope>
      );
    },
    disableContentMargin: true,
    fixedHeader: true,
    fixSiderbar: true,
    ...initialState?.settings,
    token: {
      header: {
        colorBgHeader: isDark ? '#1d1f27' : '#ffffff',
      },
      // bgLayout: isDark ? '#141414' : '#f2f3f5',
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: process.env.Ai_SAAS_API_URL,
  ...errorConfig,
};

/**
 * @name qiankun 配置
 * @description 导出 useQiankunStateForSlave 以便将主应用状态传递给子应用
 * @doc https://umijs.org/docs/max/micro-frontend#主应用透传数据
 */
export function useQiankunStateForSlave() {
  const { initialState } = useModel('@@initialState') as { initialState: any };
  const { handleMessage } = useModel('qiankunState') as { handleMessage: (msg: any) => void };
  const umiLocale = localStorage.getItem('umi_locale') || 'zh-CN';
  const localeMap: Record<string, string> = {
    'zh-CN': 'zh',
    'en-US': 'en',
    'ja-JP': 'ja',
  };
  const language = localeMap[umiLocale] || 'en';
  const token = localStorage.getItem('token');
  const navTheme = initialState?.settings?.navTheme === 'light' ? 'light' : 'dark';
  const standalone = isStandaloneApp();

  // 用 ref 稳定 permissions 引用：仅当权限内容真正变化时才更新，
  // 避免 setInitialState({ menuOpenKeys }) 触发的无关重渲染导致 Qiankun 子应用重挂载
  const permissionsRef = React.useRef<string[] | undefined>(undefined);
  const nextPermissions = initialState?.currentUser?.permissions as string[] | undefined;
  const nextStr = nextPermissions ? JSON.stringify(nextPermissions) : '';
  const prevStr = permissionsRef.current ? JSON.stringify(permissionsRef.current) : '';
  if (nextStr !== prevStr) {
    permissionsRef.current = nextPermissions;
  }
  const stablePermissions = permissionsRef.current;

  return React.useMemo(
    () => ({
      token,
      navTheme,
      permissions: stablePermissions,
      language,
      onMessage: handleMessage,
      isStandaloneApp: standalone,
      tracker,
      reporter,
      enableWebsiteBtn: false, // 隐藏ragflow的嵌入网站按钮
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, navTheme, stablePermissions, language, handleMessage, standalone],
  );
}

/**
 * 将 App.useApp() 实例桥接到全局代理，使非组件代码（如 requestErrorConfig）
 * 能通过 message-manager 消费动态主题 Context。
 */
const AppContextBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { message, notification, modal } = App.useApp();
  setMessageInstance(message);
  setNotificationInstance(notification);
  setModalInstance(modal as any);
  return <>{children}</>;
};

/**
 * 使用 antd App 组件包裹根节点，消除 "[antd: message] Static function can not consume context" 警告。
 */
export function rootContainer(container: React.ReactElement) {
  return React.createElement(
    ThemeProvider,
    null,
    React.createElement(
      App,
      null,
      React.createElement(AppContextBridge, null, container),
    ),
  );
}

export function patchClientRoutes({ routes }: { routes: any[] }) {
  // 一次遍历同时建立埋点映射和 parentKeys 映射
  initRouteMaps(routes);

  const traverse = (routes: any[]) => {
    routes.forEach((route) => {
      if (route.permission) {
        route.element = (
          <RoutePermissionGuard permission={route.permission}>
            {route.element}
          </RoutePermissionGuard>
        );
      }
      if (route.routes) {
        traverse(route.routes);
      }
    });
  };
  traverse(routes);
}
